import md5 from 'crypto-js/md5';
import { setCookie } from 'cookies-next';
import { UserDTO } from '@/shared';
import { queryClient } from '@/lib/queryClient';
import { fetchAuthMe } from '@/features/user/api/user.api';
import { userQueryKeys } from '@/features/user/api/user.keys';

const getDefaultAvatar = (gender?: number | null) => {
  const baseCdnUrl = process.env.NEXT_PUBLIC_CDN_URL + '/r/';
  if (gender === 1) return baseCdnUrl + 'blank_female.svg';
  if (gender === 2) return baseCdnUrl + 'blank_male.svg';
  return baseCdnUrl + 'blank_unknown.svg';
};

export function enrichUser(user: UserDTO): UserDTO {
  return {
    ...user,
    profileSrc: user.avatar?.length
      ? user.profileSrc
      : getDefaultAvatar(user.gender),
  };
}

export function setAutoLoginCookie(user: UserDTO) {
  const altoken = md5(
    String(user.id) + (user.username ?? '') + (user.referer ?? ''),
  ).toString();
  const converted = parseInt(String(user.id), 10).toString(36);

  setCookie('_AUTO_LOGIN', altoken + converted, {
    maxAge: 7 * 24 * 60 * 60,
    path: '/',
    domain: process.env.NEXT_PUBLIC_ROOT_DOMAIN || '.zangia.local',
    sameSite: 'lax',
  });
}

export async function getAuthMeUser(): Promise<UserDTO> {
  const user = enrichUser(await fetchAuthMe());
  setAutoLoginCookie(user);
  return user;
}

export async function refreshAuthMe(): Promise<UserDTO> {
  return queryClient.fetchQuery({
    queryKey: userQueryKeys.me,
    queryFn: getAuthMeUser,
    staleTime: 0,
  });
}

export function clearAuthMeCache() {
  queryClient.removeQueries({ queryKey: userQueryKeys.me });
}

export function decreaseNotifInCache() {
  queryClient.setQueryData<UserDTO>(userQueryKeys.me, (prev) => {
    if (!prev || prev.cnt_notif === undefined) return prev;
    return { ...prev, cnt_notif: Math.max(0, prev.cnt_notif - 1) };
  });
}

export function setUserAvatarInCache(avatar: string) {
  queryClient.setQueryData<UserDTO>(userQueryKeys.me, (prev) => {
    if (!prev) return prev;

    const profileSrc = avatar.length
      ? `${process.env.NEXT_PUBLIC_CDN_URL}/r/${Math.floor(prev.id / 1000)}/${avatar}.jpg`
      : getDefaultAvatar(prev.gender);

    return { ...prev, avatar, profileSrc };
  });
}

export function getAuthMeFromCache(): UserDTO | undefined {
  return queryClient.getQueryData<UserDTO>(userQueryKeys.me);
}
